<template>
  <audit-log
    :is-loading="isLoading"
    title="Audit Log"
    :entries="displayableEntries"
    :get-changes="getEntryChanges"
    @close="$emit('close')"
  >
    <template v-if="!hasRecordScope" #toolbar>
      <div class="d-flex align-center">
        <div class="d-flex ml-auto audit-log-form__filters my-4">
          <v-select
            v-model="sortOrder"
            :items="sortOptions"
            label="Sort by"
            item-text="text"
            item-value="value"
            dense
            hide-details
            style="max-width: 220px"
          />
          <v-select
            v-model="filterType"
            :items="filterOptions"
            label="Filter by"
            item-text="text"
            item-value="value"
            dense
            hide-details
            style="max-width: 220px"
          />
        </div>
      </div>
    </template>
  </audit-log>
</template>

<script>
  import { difference, isEqual } from 'lodash'
  import { API } from '@/utils/api'
  import { AuditLog } from '@/components/core/AuditLog'
  import {
    AUDIT_LOG_SORT_ORDER,
    AUDIT_LOG_SORT_ORDER_OPTIONS,
    AUDIT_LOG_TYPE,
    AUDIT_LOG_TYPE_OPTIONS,
  } from './constants/const'

  const NA = 'N/A'

  const RECORD_TYPE_LABELS = {
    mirror: 'Mirror',
    group_mirror: 'Group',
    redirector: 'Redirector',
    main_settings: 'Main settings',
  }

  export default {
    name: 'MirrorsRedirectorsAuditLogForm',
    components: {
      AuditLog,
    },
    props: {
      entityId: {
        type: [String, Number],
        required: true,
      },
      type: {
        type: String,
        required: true,
      },
      mirrorId: {
        type: [String, Number],
        default: null,
      },
    },
    emits: ['close'],
    data() {
      return {
        sortOrder: AUDIT_LOG_SORT_ORDER.DESC,
        filterType: this.type,
        auditLog: [],
        mirrorNameById: {},
        groupNameById: {},
        isLoading: false,
        sortOptions: AUDIT_LOG_SORT_ORDER_OPTIONS,
        filterOptions: AUDIT_LOG_TYPE_OPTIONS,
      }
    },
    watch: {
      filterType() {
        if (!this.hasRecordScope) {
          this.loadAuditLog()
        }
      },
    },
    mounted() {
      this.loadAuditLog()
    },
    computed: {
      hasRecordScope() {
        return this.mirrorId != null && this.type === AUDIT_LOG_TYPE.MIRROR
      },
      sortedAuditLog() {
        const entries = [...(this.auditLog || [])]
        const direction = this.sortOrder === AUDIT_LOG_SORT_ORDER.ASC ? 1 : -1

        return entries.sort((a, b) => {
          const timeA = new Date(a?.created || 0).getTime()
          const timeB = new Date(b?.created || 0).getTime()
          return (timeA - timeB) * direction
        })
      },
      displayableEntries() {
        return this.sortedAuditLog.filter(
          (entry) => this.getEntryChanges(entry).length > 0,
        )
      },
    },
    methods: {
      getEntryChanges(logEntry) {
        return this.compareAuditLogEntry(logEntry)
      },

      async loadAuditLog() {
        if (!this.entityId || !this.filterType) return

        this.isLoading = true
        this.auditLog = []
        try {
          const auditParams = {
            entity_id: this.entityId,
            type: this.filterType,
          }

          if (this.type === AUDIT_LOG_TYPE.MIRROR && this.mirrorId != null) {
            auditParams.mirror_id = this.mirrorId
          }

          const hasNames = Object.keys(this.mirrorNameById).length > 0
            || Object.keys(this.groupNameById).length > 0

          const requests = [
            API.call('/$v/mirrors-redirectors/audit-log/audit_log.json', auditParams),
          ]

          if (!hasNames) {
            requests.push(
              API.call('/$v/mirrors-redirectors/audit-log/name_list.json', {
                entity_id: this.entityId,
              }),
            )
          }

          const [auditResponse, namesResponse] = await Promise.all(requests)
          this.auditLog = auditResponse.data ?? []

          if (namesResponse) {
            this.mirrorNameById = this.buildNameById(namesResponse.data?.mirrors)
            this.groupNameById = this.buildNameById(namesResponse.data?.groups)
          }
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to load audit log')
        } finally {
          this.isLoading = false
        }
      },

      buildNameById(items = []) {
        return items.reduce((acc, { id, name }) => {
          if (id != null) {
            acc[id] = name
            acc[String(id)] = name
          }
          return acc
        }, {})
      },

      compareAuditLogEntry(logEntry) {
        const comparers = {
          mirror: this.compareMirror,
          group_mirror: this.compareGroupMirror,
          redirector: this.compareRedirector,
          main_settings: this.compareMainSettings,
        }
        const comparer = comparers[logEntry?.record_type] || this.compareMirror
        return comparer(logEntry)
      },

      isEmptySnapshot(snapshot) {
        return !snapshot || !Object.keys(snapshot).length
      },

      fmtList(arr) {
        return arr?.length ? arr.join(', ') : 'none'
      },

      pushEntry(changes, field, from, to) {
        changes.push(`Changed ${field} from ${from ?? NA} to ${to ?? NA}`)
      },

      pushFields(changes, from, to, keys) {
        keys.forEach((k) => {
          if (from[k] !== to[k]) {
            this.pushEntry(changes, k, from[k], to[k])
          }
        })
      },

      mirrorLabel(id) {
        const map = this.mirrorNameById
        return map[id] ?? map[String(id)] ?? `mirror ${id}`
      },

      groupLabel(id) {
        if (id == null) return NA
        const map = this.groupNameById
        return map[id] ?? map[String(id)] ?? id
      },

      pushGroupMirrorId(changes, from, to) {
        if (from.group_mirror_id !== to.group_mirror_id) {
          this.pushEntry(
            changes,
            'group_mirror_id',
            this.groupLabel(from.group_mirror_id),
            this.groupLabel(to.group_mirror_id),
          )
        }
      },

      lifecycleCheck(logEntry) {
        const { from, to, record_type, record_id } = logEntry
        const label = RECORD_TYPE_LABELS[record_type] || record_type
        const suffix = (s) => s?.name ? `: ${s.name}` : record_id != null ? ` (id ${record_id})` : ''

        if (this.isEmptySnapshot(from)) return [`Created ${label}${suffix(to)}`]
        if (this.isEmptySnapshot(to)) return [`Deleted ${label}${suffix(from)}`]
        return null
      },

      withContext(changes, prefix, name, recordId) {
        const label = name || (recordId != null ? `id ${recordId}` : null)
        if (label) changes.push(`${prefix}: ${label}`)
      },

      finalize(changes, prefix) {
        return changes.some((l) => !l.startsWith(`${prefix}:`)) ? changes : []
      },

      compareArrayDiff(changes, label, fromArr = [], toArr = []) {
        const added = difference(toArr, fromArr)
        const removed = difference(fromArr, toArr)
        if (added.length) changes.push(`Added ${label}: ${this.fmtList(added)}`)
        if (removed.length) changes.push(`Removed ${label}: ${this.fmtList(removed)}`)
      },

      compareMirrorIdArrayDiff(changes, label, fromArr = [], toArr = []) {
        const added = difference(toArr, fromArr).map((id) => this.mirrorLabel(id))
        const removed = difference(fromArr, toArr).map((id) => this.mirrorLabel(id))
        if (added.length) changes.push(`Added ${label}: ${this.fmtList(added)}`)
        if (removed.length) changes.push(`Removed ${label}: ${this.fmtList(removed)}`)
      },

      compareGeoSettings(changes, from = {}, to = {}, prefix = 'settings.geo_settings') {
        if ((from.default_rule ?? '') !== (to.default_rule ?? '')) {
          this.pushEntry(changes, `${prefix}.default_rule`, from.default_rule, to.default_rule)
        }

        if (!isEqual(from.redirect, to.redirect)) {
          this.compareArrayDiff(changes, `${prefix}.redirect`, from.redirect, to.redirect)
        }
        if (!isEqual(from.blocked_geo, to.blocked_geo)) {
          this.compareArrayDiff(changes, `${prefix}.blocked_geo`, from.blocked_geo, to.blocked_geo)
        }
        if (!isEqual(from.allowed_geo, to.allowed_geo)) {
          this.compareArrayDiff(changes, `${prefix}.allowed_geo`, from.allowed_geo, to.allowed_geo)
        }
      },

      compareRedirectorRules(changes, from = {}, to = {}) {
        const keys = new Set([...Object.keys(from), ...Object.keys(to)])
        for (const id of keys) {
          const label = this.mirrorLabel(id)
          const fromItems = from[id]?.items ?? []
          const toItems = to[id]?.items ?? []
          if (!from[id]) {
            changes.push(`Added rules for mirror ${label}: ${this.fmtList(toItems)}`)
            continue
          }
          if (!to[id]) {
            changes.push(`Removed rules for mirror ${label}`)
            continue
          }
          if (isEqual(fromItems, toItems)) continue
          this.compareArrayDiff(changes, `countries for mirror ${label}`, fromItems, toItems)
        }
      },

      compareIpList(changes, from = [], to = []) {
        const fromByIp = Object.fromEntries(from.map((i) => [i.ip, i]))
        const toByIp = Object.fromEntries(to.map((i) => [i.ip, i]))
        for (const ip of new Set([...Object.keys(fromByIp), ...Object.keys(toByIp)])) {
          if (!fromByIp[ip]) {
            const comment = toByIp[ip]?.comment
            changes.push(`Added IP ${ip}${comment ? ` (${comment})` : ''}`)
            continue
          }
          if (!toByIp[ip]) {
            changes.push(`Removed IP ${ip}`)
            continue
          }
          if (fromByIp[ip].comment !== toByIp[ip].comment) {
            this.pushEntry(changes, `IP ${ip} comment`, fromByIp[ip].comment, toByIp[ip].comment)
          }
        }
      },

      compareMirror(logEntry) {
        const lc = this.lifecycleCheck(logEntry)
        if (lc) return lc
        const { from, to } = logEntry
        const changes = []
        this.withContext(changes, 'Mirror', to?.name || from?.name, logEntry.record_id)
        this.pushFields(changes, from, to, ['status', 'name'])
        this.pushGroupMirrorId(changes, from, to)
        const fromSettings = from.settings || {}
        const toSettings = to.settings || {}
        this.pushFields(changes, fromSettings, toSettings, ['comment', 'send_email'])
        this.compareGeoSettings(changes, fromSettings.geo_settings, toSettings.geo_settings)
        return this.finalize(changes, 'Mirror')
      },

      compareGroupMirror(logEntry) {
        const lc = this.lifecycleCheck(logEntry)
        if (lc) return lc
        const { from, to } = logEntry
        const changes = []
        this.withContext(changes, 'Group', to?.name || from?.name, logEntry.record_id)
        this.pushFields(changes, from, to, ['name'])
        if (from.primary_mirror_id !== to.primary_mirror_id) {
          this.pushEntry(
            changes,
            'primary_mirror_id',
            this.mirrorLabel(from.primary_mirror_id),
            this.mirrorLabel(to.primary_mirror_id),
          )
        }
        const fromMirrors = from.mirrors?.items ?? []
        const toMirrors = to.mirrors?.items ?? []
        if (!isEqual(fromMirrors, toMirrors)) {
          this.compareMirrorIdArrayDiff(changes, 'mirrors', fromMirrors, toMirrors)
        }
        const fromSettings = from.settings || {}
        const toSettings = to.settings || {}
        this.compareGeoSettings(changes, fromSettings.geo_settings, toSettings.geo_settings)
        return this.finalize(changes, 'Group')
      },

      compareRedirector(logEntry) {
        const lc = this.lifecycleCheck(logEntry)
        if (lc) return lc
        const { from, to } = logEntry
        const changes = []
        this.withContext(changes, 'Redirector', to?.name || from?.name, logEntry.record_id)
        this.pushFields(changes, from, to, ['status', 'name'])
        this.pushGroupMirrorId(changes, from, to)
        this.compareRedirectorRules(changes, from.rules, to.rules)
        return this.finalize(changes, 'Redirector')
      },

      compareMainSettings(logEntry) {
        const lc = this.lifecycleCheck(logEntry)
        if (lc) return lc
        const { from, to } = logEntry
        const changes = []
        this.withContext(changes, 'Main settings', to?.name || from?.name, logEntry.record_id)
        this.pushFields(changes, from, to, ['key', 'name'])
        const fromSettings = from.settings || {}
        const toSettings = to.settings || {}
        this.pushFields(changes, fromSettings, toSettings, ['login', 'prefix'])
        this.compareIpList(changes, fromSettings.ip_list?.items, toSettings.ip_list?.items)
        return this.finalize(changes, 'Main settings')
      },
    },
  }
</script>

<style lang="scss" scoped>
  .audit-log-form__filters {
    gap: 16px;
  }
</style>
